import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';

/**
 * @see https://www.figma.com/file/o3FnMX3UGDDAL5Lblv3rdz/Retail---CZR-New-Type-%26-Components?type=design&node-id=1-688&mode=design&t=Jh0Utr62LxizwkLk-0
 */

/**
 * regular = 400
 * medium = 500
 * bold = 600
 * black = 700
 */
type FontWeight = 'regular' | 'medium' | 'bold' | 'black' | number;

type Defined<T> = T extends undefined ? never : T;

export type TypographyProps = {
  as?: string;

  /**
   * @default 'roboto-flex'
   */
  font?: 'roboto-flex' | 'oswald';

  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | 'top-sm'
    | 'top-rg'
    | 'top-lg'
    | 'videowall-xs'
    | 'videowall-sm'
    | 'videowall-md'
    | 'videowall-lg'
    | 'videowall-xl'
    | 'videowall-text';

  weight?: FontWeight;

  stretch?: CSSProperties['fontStretch'];
  textAlign?: CSSProperties['textAlign'];
  lineHeight?: CSSProperties['lineHeight'];
  margin?: CSSProperties['margin'];
};

const getFontFamily = (font: Defined<TypographyProps['font']>) => {
  if (font === 'oswald') {
    return `'oswald', sans-serif`;
  }
  return `'roboto-flex', sans-serif`;
};

export const robotoFonts = {
  xs: '1.2rem', // body-xs
  sm: '1.4rem', // body-sm
  md: '1.6rem', // body
  lg: '1.8rem', // body-lg
  xl: '2rem', // heading-lg
  '2xl': '2.4rem', // heading-xl
  '3xl': '3.2rem', // heading-2xl
  '4xl': '4.8rem', // Example: Device Update Countdown

  'top-sm': '3.6rem',
  'top-rg': '4.8rem',
  'top-lg': '6.2rem',

  'videowall-xs': '3rem',
  'videowall-sm': '3.2rem',
  'videowall-md': '4.4rem',
  'videowall-lg': '6rem',
  'videowall-xl': '6.8rem',

  'videowall-text': '4.6rem'
} as const;

export const oswaldFonts = {
  xs: '1.4rem', // display-xs
  sm: '1.6rem', // display-sm
  md: '1.8rem', // display-md
  lg: '2.2rem', // display-lg
  xl: '3rem', // display-xl
  '2xl': '3.8rem', // display-2xl - This has a smaller size at bold. Currently the same
  '3xl': '4.6rem', //display videowall size
  'top-sm': '3.8rem', //TOP = Top Screen Font - No matching figma size
  'top-rg': '4.8rem', //top-body - doesnt match figma is closest
  'top-lg': '5.8rem', //top-lg-header

  'videowall-text': '4.6rem' //videowall
} as const;

const getFontSize = (font: Defined<TypographyProps['font']>, size: Defined<TypographyProps['size']>): string => {
  if (font === 'oswald') return oswaldFonts[size];
  return robotoFonts[size];
};

const getFontWeight = (weight: FontWeight) => {
  const weightMap: Record<FontWeight, number> = {
    regular: 400,
    medium: 500,
    bold: 600,
    black: 700
  };

  return weightMap[weight] ?? weight;
};

const typographyStyles = ({
  font = 'roboto-flex',
  size,
  weight,
  lineHeight,
  stretch,
  textAlign,
  margin
}: TypographyProps) => {
  return css`
    /**
   * below css value is set on the body so going to inherit from that
   * line-height: 1.2;
   */
    font-family: ${getFontFamily(font)};

    ${!size
      ? css``
      : css`
          font-size: ${getFontSize(font, size)};
        `}

    ${!weight
      ? css``
      : css`
          font-weight: ${getFontWeight(weight)};
        `}

  ${!lineHeight
      ? css``
      : css`
          line-height: ${lineHeight};
        `}

  ${(() => {
      const stretchMap = {
        '3xl': '60%'
      };
      if (font !== 'roboto-flex') return css``;

      if (!size || stretchMap[size] === undefined) {
        return css`
          font-stretch: 25%;
        `;
      }
      return `font-stretch: ${stretchMap[size]};`;
    })()}


  ${!stretch
      ? css``
      : css`
          font-stretch: ${stretch};
        `}

  ${!textAlign
      ? css``
      : css`
          text-align: ${textAlign};
        `}

  ${!margin
      ? css``
      : css`
          margin: ${margin};
        `}
  `;
};

export const Typography = styled.span<TypographyProps>`
  ${props => typographyStyles(props)}
`;

export type DisplayHeadingProps = Omit<TypographyProps, 'size' | 'font'> & {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'top-lg';
};

export const DisplayHeading = styled.h2<DisplayHeadingProps>`
  ${props => typographyStyles({ ...props, font: 'oswald' })}
`;

type HeadingProps = Omit<TypographyProps, 'size' | 'font'> & {
  size?: 'lg' | 'xl' | '2xl' | '3xl';
};

export const Heading = styled.h2<HeadingProps>`
  ${({ as = 'h2', size = 'lg', ...props }) => {
    const sizeMap = {
      lg: 'xl',
      xl: '2xl',
      '2xl': '3xl',
      '3xl': '3xl',
      'top-lg': 'top-lg'
    } as const;

    return typographyStyles({ ...props, as, size: sizeMap[size] });
  }}
`;

export type TextProps = Omit<TypographyProps, 'size' | 'font'> & {
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | '4xl'
    | 'videowall-xs'
    | 'videowall-sm'
    | 'videowall-md'
    | 'videowall-lg'
    | 'videowall-xl';
};

export type TopScreenTextProps = Omit<TypographyProps, 'size' | 'font'> & {
  size?: 'top-sm' | 'top-rg' | 'top-lg';
};

export type TopScreenHeadingProps = Omit<TypographyProps, 'size' | 'font'> & {
  size?: 'top-sm' | 'top-rg' | 'top-lg';
};

export const Label = styled.label<TypographyProps>`
  ${props => typographyStyles(props)}
`;

export const Text = styled.span<TextProps>`
  ${props => typographyStyles(props)}
`;

export const TopScreenText = styled.span<TopScreenTextProps>`
  ${props => typographyStyles(props)}
`;

export const TopScreenHeading = styled.span<TopScreenHeadingProps>`
  ${props => typographyStyles({ ...props, font: 'oswald' })}
`;

export const Block = styled.div<TextProps>`
  ${props => typographyStyles(props)}
`;
